import {
  canonicalContentHash,
  CapabilityManifestSchema,
  DeclarativeCapabilityAuthoringRegistrationSchema,
  type ProductContext,
  ViewProviderDescriptorSchema,
} from "@inf-monkeys-tech/monkeys";
import {
  compileDesignCapabilityCatalogArtifact as compileSdkDesignCapabilityCatalogArtifact,
  compileViewProviderRegistry,
} from "@inf-monkeys-tech/monkeys/runtime";

import type {
  CompileDesignCapabilityCatalogArtifactInput,
  CompiledDesignCapabilityCatalog,
  CompiledDesignCapabilityCatalogArtifact,
  DesignCapabilityCatalogEntry,
} from "./contracts";

export const DESIGN_CAPABILITY_CATALOG_SOURCE_REF =
  "monkeys.design.capabilities";
export const DESIGN_CAPABILITY_CATALOG_REVISION = 105;

const EDITOR_CATEGORIES = new Set([
  "layout",
  "navigation",
  "data-view",
  "data-editor",
  "content",
  "status",
  "professional",
]);

const requireNonEmptyKey = (value: string, field: string) => {
  if (!value.trim() || !value.includes(".")) {
    throw new TypeError(`${field} must be a non-empty semantic i18n key.`);
  }
};

const sameVersion = (left: string | number | undefined, right: string) =>
  left === undefined || String(left) === right;

const revisionRefKey = (reference: {
  ownerRepo: string;
  id: string;
  revision: string | number;
  contentHash: string;
}) =>
  `${reference.ownerRepo}:${reference.id}@${reference.revision}:${reference.contentHash}`;

const validateEntry = (
  input: DesignCapabilityCatalogEntry,
): DesignCapabilityCatalogEntry => {
  const manifest = CapabilityManifestSchema.parse(input.manifest);
  const provider = ViewProviderDescriptorSchema.parse(input.provider);
  const declarativeRegistration =
    DeclarativeCapabilityAuthoringRegistrationSchema.parse(
      input.declarativeRegistration,
    );
  const editor = Object.freeze({
    ...input.editor,
    slots: Object.freeze([...input.editor.slots]),
    ontologyCompatibility: Object.freeze(
      input.editor.ontologyCompatibility.map((compatibility) =>
        Object.freeze({ ...compatibility }),
      ),
    ),
  });

  requireNonEmptyKey(editor.labelKey, "editor.labelKey");
  requireNonEmptyKey(editor.descriptionKey, "editor.descriptionKey");
  if (
    typeof editor.eligible !== "boolean" ||
    editor.eligible !== declarativeRegistration.editorEligible ||
    !EDITOR_CATEGORIES.has(editor.category)
  ) {
    throw new TypeError(
      `Capability ${manifest.id} has invalid editor eligibility metadata.`,
    );
  }
  if (provider.capabilityRef.id !== manifest.id) {
    throw new TypeError(
      `Provider ${provider.providerId} targets ${provider.capabilityRef.id}, not ${manifest.id}.`,
    );
  }
  if (
    declarativeRegistration.capabilityRevisionRef.id !== manifest.id ||
    declarativeRegistration.capabilityRevisionRef.ownerRepo !==
      manifest.ownerRepo ||
    String(declarativeRegistration.capabilityRevisionRef.revision) !==
      String(manifest.capabilityVersion)
  ) {
    throw new TypeError(
      `Capability ${manifest.id} authoring registration does not pin its exact manifest identity.`,
    );
  }
  if (
    declarativeRegistration.providerRevisionRef.id !== provider.providerId ||
    declarativeRegistration.providerRevisionRef.ownerRepo !==
      provider.ownerRepo ||
    String(declarativeRegistration.providerRevisionRef.revision) !==
      String(provider.providerVersion)
  ) {
    throw new TypeError(
      `Capability ${manifest.id} authoring registration does not pin its exact provider identity.`,
    );
  }
  if (
    declarativeRegistration.capabilityRevisionRef.contentHash !==
      canonicalContentHash(manifest) ||
    declarativeRegistration.providerRevisionRef.contentHash !==
      canonicalContentHash(provider)
  ) {
    throw new TypeError(
      `Capability ${manifest.id} authoring registration has stale capability or provider content hashes.`,
    );
  }
  const { registrationRevisionRef, sourceContentHash, ...registrationSource } =
    declarativeRegistration;
  if (
    sourceContentHash !== canonicalContentHash(registrationSource) ||
    registrationRevisionRef.contentHash !== sourceContentHash
  ) {
    throw new TypeError(
      `Capability ${manifest.id} authoring registration source hash is not exact.`,
    );
  }
  const schemaDocuments = new Map(
    declarativeRegistration.schemaDocuments.map((entry) => [
      revisionRefKey(entry.schemaRevisionRef),
      entry,
    ]),
  );
  const requiredSchemaReferences = [
    declarativeRegistration.propertySchemaRevisionRef,
    ...(declarativeRegistration.uiSchemaRevisionRef
      ? [declarativeRegistration.uiSchemaRevisionRef]
      : []),
    ...declarativeRegistration.inputPorts.map((port) => port.schemaRevisionRef),
    ...declarativeRegistration.outputPorts.map(
      (port) => port.schemaRevisionRef,
    ),
    ...declarativeRegistration.ontologyRequirements.map(
      (requirement) => requirement.renderModelSchemaRevisionRef,
    ),
  ];
  const requiredSchemaKeys = new Set(
    requiredSchemaReferences.map(revisionRefKey),
  );
  if (
    schemaDocuments.size !== declarativeRegistration.schemaDocuments.length ||
    schemaDocuments.size !== requiredSchemaKeys.size
  ) {
    throw new TypeError(
      `Capability ${manifest.id} authoring registration must publish one canonical document for each exact schema reference.`,
    );
  }
  for (const reference of requiredSchemaReferences) {
    const entry = schemaDocuments.get(revisionRefKey(reference));
    if (!entry) {
      throw new TypeError(
        `Capability ${manifest.id} has no canonical document for schema ${reference.id}.`,
      );
    }
    if (
      entry.schemaRevisionRef.kind !== "schema" ||
      entry.schemaRevisionRef.contentHash !==
        canonicalContentHash(entry.document) ||
      entry.document.$schema !==
        "https://json-schema.org/draft/2020-12/schema" ||
      entry.document.$id !== entry.schemaRevisionRef.id ||
      typeof entry.document.type !== "string"
    ) {
      throw new TypeError(
        `Capability ${manifest.id} schema ${reference.id} is not an exact canonical JSON Schema document.`,
      );
    }
  }
  if (
    !sameVersion(provider.capabilityRef.version, manifest.capabilityVersion)
  ) {
    throw new TypeError(
      `Provider ${provider.providerId} does not target capability version ${manifest.capabilityVersion}.`,
    );
  }

  const binding = manifest.runtime.providerBindings.find(
    ({ providerRef }) => providerRef.id === provider.providerId,
  );
  if (
    !binding ||
    !sameVersion(binding.providerRef.version, provider.providerVersion)
  ) {
    throw new TypeError(
      `Capability ${manifest.id} does not explicitly bind provider ${provider.providerId}@${provider.providerVersion}.`,
    );
  }
  if (
    provider.loading !== manifest.runtime.loading ||
    provider.stateOwner !== manifest.runtime.stateOwner
  ) {
    throw new TypeError(
      `Provider ${provider.providerId} lifecycle does not match capability ${manifest.id}.`,
    );
  }

  const registrationInputPorts = new Map(
    declarativeRegistration.inputPorts.map((port) => [port.name, port]),
  );
  const registrationOutputPorts = new Map(
    declarativeRegistration.outputPorts.map((port) => [port.name, port]),
  );
  if (
    registrationInputPorts.size !== manifest.ports.inputs.length ||
    manifest.ports.inputs.some((port) => {
      const registrationPort = registrationInputPorts.get(port.name);
      return (
        !registrationPort ||
        registrationPort.required !== port.required ||
        registrationPort.multiple !== port.multiple
      );
    }) ||
    registrationOutputPorts.size !== manifest.ports.outputs.length ||
    manifest.ports.outputs.some((port) => {
      const registrationPort = registrationOutputPorts.get(port.name);
      return (
        !registrationPort ||
        registrationPort.required !== port.required ||
        registrationPort.multiple !== port.multiple
      );
    })
  ) {
    throw new TypeError(
      `Capability ${manifest.id} authoring ports differ from its manifest ports.`,
    );
  }
  if (
    declarativeRegistration.allowedSideEffects.length !==
      manifest.runtime.sideEffects.length ||
    declarativeRegistration.allowedSideEffects.some(
      (sideEffect) => !manifest.runtime.sideEffects.includes(sideEffect),
    )
  ) {
    throw new TypeError(
      `Capability ${manifest.id} authoring side effects differ from its manifest.`,
    );
  }
  if (
    provider.sideEffects.length !== manifest.runtime.sideEffects.length ||
    provider.sideEffects.some(
      (sideEffect) => !manifest.runtime.sideEffects.includes(sideEffect),
    )
  ) {
    throw new TypeError(
      `Provider ${provider.providerId} side effects differ from capability ${manifest.id}.`,
    );
  }
  if (
    provider.performance.budgetMs !== undefined &&
    manifest.observability.performanceBudgetMs !== undefined &&
    provider.performance.budgetMs !== manifest.observability.performanceBudgetMs
  ) {
    throw new TypeError(
      `Provider ${provider.providerId} performance budget differs from capability ${manifest.id}.`,
    );
  }

  const declaredSlots = new Set(manifest.placement.slots);
  const uniqueEditorSlots = new Set(editor.slots);
  if (uniqueEditorSlots.size !== editor.slots.length) {
    throw new TypeError(
      `Capability ${manifest.id} editor slots must be unique.`,
    );
  }
  for (const slot of editor.slots) {
    if (!declaredSlots.has(slot)) {
      throw new TypeError(
        `Capability ${manifest.id} editor slot ${slot} is absent from its manifest.`,
      );
    }
  }
  if (
    declaredSlots.size !== uniqueEditorSlots.size ||
    [...declaredSlots].some((slot) => !uniqueEditorSlots.has(slot))
  ) {
    throw new TypeError(
      `Capability ${manifest.id} editor slots must exactly match its manifest slots.`,
    );
  }

  const inputPorts = new Map(
    manifest.ports.inputs.map((port) => [port.name, port.schemaRef]),
  );
  const compatibilityPorts = new Set<string>();
  for (const compatibility of editor.ontologyCompatibility) {
    if (compatibilityPorts.has(compatibility.inputPort)) {
      throw new TypeError(
        `Capability ${manifest.id} has duplicate Ontology compatibility for ${compatibility.inputPort}.`,
      );
    }
    compatibilityPorts.add(compatibility.inputPort);
    if (inputPorts.get(compatibility.inputPort) !== compatibility.schemaRef) {
      throw new TypeError(
        `Capability ${manifest.id} Ontology compatibility for ${compatibility.inputPort} is not backed by the declared input port schema.`,
      );
    }
  }

  return Object.freeze({
    manifest,
    provider,
    editor,
    declarativeRegistration,
  });
};

export const compileDesignCapabilityCatalog = (
  inputs: readonly DesignCapabilityCatalogEntry[],
): CompiledDesignCapabilityCatalog => {
  const entries = Object.freeze(inputs.map(validateEntry));
  const manifestsById = new Map<
    string,
    DesignCapabilityCatalogEntry["manifest"]
  >();
  const entriesByProviderId = new Map<string, DesignCapabilityCatalogEntry>();
  const groupedEntries = new Map<string, DesignCapabilityCatalogEntry[]>();

  for (const entry of entries) {
    const existingManifest = manifestsById.get(entry.manifest.id);
    if (
      existingManifest &&
      JSON.stringify(existingManifest) !== JSON.stringify(entry.manifest)
    ) {
      throw new TypeError(
        `Capability ${entry.manifest.id} has conflicting catalog manifests.`,
      );
    }
    manifestsById.set(entry.manifest.id, entry.manifest);
    if (entriesByProviderId.has(entry.provider.providerId)) {
      throw new TypeError(`Duplicate provider ${entry.provider.providerId}.`);
    }
    entriesByProviderId.set(entry.provider.providerId, entry);
    const capabilityEntries = groupedEntries.get(entry.manifest.id) ?? [];
    capabilityEntries.push(entry);
    groupedEntries.set(entry.manifest.id, capabilityEntries);
  }

  const manifests = Object.freeze([...manifestsById.values()]);
  const providers = Object.freeze(entries.map(({ provider }) => provider));
  const sdkRegistry = compileViewProviderRegistry({
    capabilities: manifests,
    providers,
  });
  const entriesByCapabilityId = new Map(
    [...groupedEntries].map(([capabilityId, capabilityEntries]) => [
      capabilityId,
      Object.freeze([...capabilityEntries]),
    ]),
  );

  const requireProvider = (providerId: string) => {
    const entry = entriesByProviderId.get(providerId);
    if (!entry)
      throw new TypeError(`Unknown Design capability provider ${providerId}.`);
    return entry;
  };

  return Object.freeze({
    entries,
    manifests,
    providers,
    entriesByProviderId,
    entriesByCapabilityId,
    requireProvider,
    resolve(
      capabilityId: string,
      product: ProductContext,
      preferredProviderId?: string,
    ) {
      const provider = sdkRegistry.resolveProvider(
        capabilityId,
        product,
        preferredProviderId,
      );
      return requireProvider(provider.providerId);
    },
  });
};

export const compileDesignCapabilityCatalogArtifact = ({
  catalog,
  sourceRef = DESIGN_CAPABILITY_CATALOG_SOURCE_REF,
  revision = DESIGN_CAPABILITY_CATALOG_REVISION,
}: CompileDesignCapabilityCatalogArtifactInput): CompiledDesignCapabilityCatalogArtifact =>
  compileSdkDesignCapabilityCatalogArtifact({
    sourceRef,
    revision,
    entries: catalog.entries.map(
      ({ manifest, provider, declarativeRegistration }) => ({
        manifest,
        provider,
        declarativeRegistration,
      }),
    ),
  });
